import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaSave, FaPlus, FaTimes, FaUndo } from 'react-icons/fa';
import { apiMasterAPI, settingsAPI } from '../services/api';
import { CLEANABILITY_CONFIG_KEY, DEFAULT_CLEANABILITY_CONFIG } from '../utils/cleanabilityAssessment';

const clone = (value) => JSON.parse(JSON.stringify(value));

// App Configuration → Master Data Rules: the questions and rating bands used by
// API Master's cleanability scoring helper ("Help me rate"). Saved APIs keep the
// question text and scores they were rated with, so editing this only affects
// assessments made afterwards.
const CleanabilityAssessmentConfigEditor = () => {
  const [config, setConfig] = useState(clone(DEFAULT_CLEANABILITY_CONFIG));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiMasterAPI.getCleanabilityConfig()
      .then((res) => { if (res?.data?.data?.questions?.length) setConfig(clone(res.data.data)); })
      .catch(() => {});
  }, []);

  const updateQuestion = (qi, patch) => setConfig((c) => {
    const next = clone(c);
    next.questions[qi] = { ...next.questions[qi], ...patch };
    return next;
  });
  const updateOption = (qi, oi, patch) => setConfig((c) => {
    const next = clone(c);
    next.questions[qi].options[oi] = { ...next.questions[qi].options[oi], ...patch };
    return next;
  });

  const scoreRange = config.questions.reduce((acc, q) => {
    const scores = q.options.map((o) => Number(o.score) || 0);
    return { min: acc.min + Math.min(...scores), max: acc.max + Math.max(...scores) };
  }, { min: 0, max: 0 });

  const save = async () => {
    const problems = [];
    config.questions.forEach((q, i) => {
      if (!String(q.label || '').trim()) problems.push(`Question ${i + 1} has no text`);
      if (q.options.length < 2) problems.push(`Question ${i + 1} needs at least 2 answers`);
      q.options.forEach((o, j) => {
        if (!String(o.label || '').trim()) problems.push(`Question ${i + 1}, answer ${j + 1} has no text`);
        if (!Number.isFinite(Number(o.score)) || String(o.score).trim() === '') problems.push(`Question ${i + 1}, answer ${j + 1} needs a numeric score`);
      });
    });
    const easyMax = Number(config.bands.easy_max);
    const mediumMax = Number(config.bands.medium_max);
    if (!config.questions.length) problems.push('Add at least one question');
    if (!Number.isFinite(easyMax) || !Number.isFinite(mediumMax) || easyMax >= mediumMax) problems.push('Easy upper limit must be below the Medium upper limit');
    if (problems.length) return toast.warning(problems[0]);

    setSaving(true);
    try {
      const clean = {
        questions: config.questions.map((q) => ({
          key: q.key,
          label: q.label.trim(),
          options: q.options.map((o) => ({ label: o.label.trim(), score: Number(o.score) })),
        })),
        bands: { easy_max: easyMax, medium_max: mediumMax },
      };
      await settingsAPI.updateSetting(CLEANABILITY_CONFIG_KEY, JSON.stringify(clean));
      toast.success('Cleanability scoring helper saved');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const smallBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', padding: 0, border: 'none', background: 'transparent', color: 'var(--danger-text)', cursor: 'pointer' };

  return (
    <div className="card" style={{ marginTop: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <div>
          <h3 style={{ margin: 0 }}>Cleanability Scoring Helper</h3>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
            Questions used by "Help me rate" in API Master. Each answer adds its score; the total maps to Easy / Medium / Difficult.
            APIs already rated keep the questions they were rated with.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-outline" onClick={() => setConfig(clone(DEFAULT_CLEANABILITY_CONFIG))} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaUndo /> Defaults
          </button>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaSave /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {config.questions.map((q, qi) => (
        <div key={q.key} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
            <strong style={{ minWidth: '20px' }}>{qi + 1}.</strong>
            <input className="form-control form-control-sm" value={q.label} onChange={(e) => updateQuestion(qi, { label: e.target.value })} placeholder="Question" />
            <button type="button" style={smallBtn} title="Remove question" onClick={() => setConfig((c) => ({ ...c, questions: c.questions.filter((_, i) => i !== qi) }))}><FaTimes /></button>
          </div>
          {q.options.map((o, oi) => (
            <div key={oi} style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '4px 0 0 28px' }}>
              <input className="form-control form-control-sm" value={o.label} onChange={(e) => updateOption(qi, oi, { label: e.target.value })} placeholder="Answer" />
              <input className="form-control form-control-sm" type="number" value={o.score} onChange={(e) => updateOption(qi, oi, { score: e.target.value })} style={{ width: '80px', flex: '0 0 80px' }} title="Score" />
              <button type="button" style={smallBtn} title="Remove answer" onClick={() => updateQuestion(qi, { options: q.options.filter((_, i) => i !== oi) })}><FaTimes /></button>
            </div>
          ))}
          <button type="button" className="btn btn-outline btn-sm" style={{ margin: '6px 0 0 28px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            onClick={() => updateQuestion(qi, { options: [...q.options, { label: '', score: q.options.length + 1 }] })}>
            <FaPlus /> Answer
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        onClick={() => setConfig((c) => ({ ...c, questions: [...c.questions, { key: `q_${Date.now()}`, label: '', options: [{ label: '', score: 1 }, { label: '', score: 2 }, { label: '', score: 3 }] }] }))}>
        <FaPlus /> Question
      </button>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap', marginTop: '14px' }}>
        <div>
          <label className="form-label">Easy if total ≤</label>
          <input className="form-control form-control-sm" type="number" value={config.bands.easy_max} onChange={(e) => setConfig((c) => ({ ...c, bands: { ...c.bands, easy_max: e.target.value } }))} style={{ width: '100px' }} />
        </div>
        <div>
          <label className="form-label">Medium if total ≤</label>
          <input className="form-control form-control-sm" type="number" value={config.bands.medium_max} onChange={(e) => setConfig((c) => ({ ...c, bands: { ...c.bands, medium_max: e.target.value } }))} style={{ width: '100px' }} />
        </div>
        <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', paddingBottom: '6px' }}>
          Above that: Difficult. Possible total: {scoreRange.min}–{scoreRange.max}.
        </div>
      </div>
    </div>
  );
};

export default CleanabilityAssessmentConfigEditor;
